import type { MultiPayload } from "@defuse-protocol/contract-types"
import {
  type authHandle,
  messageFactory,
  type walletMessage,
} from "@defuse-protocol/internal-utils"
import { base64 } from "@scure/base"
import { bridgeSDK } from "@src/components/DefuseSDK/constants/bridgeSdk"
import { computeAppFeeBps } from "@src/components/DefuseSDK/utils/appFee"
import { assert } from "@src/components/DefuseSDK/utils/assert"
import { APP_FEE_BPS } from "@src/utils/environment"
import type { FeeRecipientSplit } from "@src/utils/getAppFeeRecipient"
import { logger } from "@src/utils/logger"
import { splitAppFee } from "@src/utils/splitAppFee"
import { assertEvent, assign, fromPromise, setup } from "xstate"
import {
  type SignerCredentials,
  formatSignedIntent,
  formatUserIdentity,
} from "../../../core/formatters"
import { calculateSplitAmounts } from "../../../sdk/aggregatedQuote/calculateSplitAmounts"
import { AmountMismatchError } from "../../../sdk/aggregatedQuote/errors/amountMismatchError"
import type { BaseTokenInfo, TokenInfo, TokenValue } from "../../../types/base"
import { findError } from "../../../utils/errors"
import {
  adjustDecimals,
  getAnyBaseTokenInfo,
  getUnderlyingBaseTokenInfos,
  netDownAmount,
} from "../../../utils/tokenUtils"
import type { BalanceMapping } from "../../machines/depositedBalanceMachine"
import {
  type Errors as SignIntentErrors,
  type Output as SignIntentMachineOutput,
  signIntentMachine,
} from "../../machines/signIntentMachine"
import type { SignMessage } from "../types/sharedTypes"
import { type Expiry, expiryToSeconds } from "../utils/expiryUtils"

export type OTCMakerSignActorInput = {
  parsed: {
    tokenIn: TokenInfo
    tokenOut: BaseTokenInfo
    amountIn: TokenValue
    amountOut: TokenValue
    expiry: Expiry
  }
  balances: BalanceMapping
  signerCredentials: SignerCredentials
  signMessage: SignMessage
  referral: string | undefined
  appFeeRecipients: FeeRecipientSplit[]
}

export type OTCMakerSignActorOutput =
  | { tag: "err"; value: OTCMakerSignActorErrors }
  | { tag: "ok"; value: OTCMakerSignActorSuccess }

export type OTCMakerSignActorSuccess = {
  multiPayload: MultiPayload
  signatureResult: walletMessage.WalletSignatureResult
  signerCredentials: SignerCredentials
  usedNonceBase64: string
}

export type OTCMakerSignActorContext = OTCMakerSignActorInput & {
  nonce: Uint8Array | null
  walletMessage: walletMessage.WalletMessage | null
}

type FailedToPrepareMessageToSignError = {
  reason: "ERR_PREPARING_SIGNING_DATA"
}

export type OTCMakerSignActorErrors =
  | SignIntentErrors
  | FailedToPrepareMessageToSignError

export function applyMakerAppFee(
  tokenInAmounts: Record<BaseTokenInfo["defuseAssetId"], bigint>,
  appFeeBps: number
): {
  tokenInDiff: Record<BaseTokenInfo["defuseAssetId"], bigint>
  appFee: [BaseTokenInfo["defuseAssetId"], bigint][]
} {
  const tokenInDiff: Record<BaseTokenInfo["defuseAssetId"], bigint> = {}
  const appFee: [BaseTokenInfo["defuseAssetId"], bigint][] = []

  for (const [assetId, grossAmount] of Object.entries(tokenInAmounts)) {
    const netAmount =
      appFeeBps > 0 ? netDownAmount(grossAmount, appFeeBps * 100) : grossAmount
    tokenInDiff[assetId] = -netAmount

    const feeAmount = grossAmount - netAmount
    if (feeAmount > 0n) {
      appFee.push([assetId, feeAmount])
    }
  }

  return { tokenInDiff, appFee }
}

export const otcMakerSignMachine = setup({
  types: {
    input: {} as OTCMakerSignActorInput,
    output: {} as OTCMakerSignActorOutput,
    context: {} as OTCMakerSignActorContext,
    events: {} as
      | { type: "xstate.init"; input: OTCMakerSignActorInput }
      | {
          type: "COMPLETE"
          output:
            | SignIntentMachineOutput
            | { tag: "err"; value: FailedToPrepareMessageToSignError }
        },
    children: {} as {
      signRef: "signActor"
    },
  },
  actors: {
    signActor: signIntentMachine,
    prepareSignData: fromPromise(
      async ({
        input,
      }: { input: OTCMakerSignActorContext }): Promise<{
        nonce: Uint8Array
        walletMessage: walletMessage.WalletMessage
      }> => {
        const { nonce, deadline } = await bridgeSDK
          .intentBuilder()
          .setDeadline(
            new Date(Date.now() + expiryToSeconds(input.parsed.expiry) * 1000)
          )
          .build()

        let tokenInAmounts: Record<BaseTokenInfo["defuseAssetId"], bigint>

        try {
          tokenInAmounts = calculateSplitAmounts(
            getUnderlyingBaseTokenInfos(input.parsed.tokenIn),
            input.parsed.amountIn,
            input.balances
          )
        } catch (err: unknown) {
          if (!findError(err, AmountMismatchError)) {
            throw err
          }

          /**
           * If user has insufficient balance, we will generate a message with the full amount,
           * and let the user know that they have insufficient balance.
           */
          const tokenIn = getAnyBaseTokenInfo(input.parsed.tokenIn)
          tokenInAmounts = {
            [tokenIn.defuseAssetId]: adjustDecimals(
              input.parsed.amountIn.amount,
              input.parsed.amountIn.decimals,
              tokenIn.decimals
            ),
          }
        }

        const primaryRecipient = input.appFeeRecipients[0]?.recipient ?? ""
        const currentUser: authHandle.AuthHandle = {
          identifier: input.signerCredentials.credential,
          method: input.signerCredentials.credentialType,
        }
        const appFeeBps =
          input.appFeeRecipients.length > 0
            ? computeAppFeeBps(
                APP_FEE_BPS,
                input.parsed.tokenIn,
                input.parsed.tokenOut,
                primaryRecipient,
                currentUser
              )
            : 0
        const { tokenInDiff, appFee } = applyMakerAppFee(
          tokenInAmounts,
          appFeeBps
        )
        const {
          primaryAppFee,
          primaryRecipient: appFeeRecipient,
          transferIntents,
        } = appFee.length > 0
          ? splitAppFee(appFee, input.appFeeRecipients)
          : {
              primaryAppFee: [],
              primaryRecipient: "",
              transferIntents: [],
            }
        const decodedNonce = base64.decode(nonce)
        const innerMessage = messageFactory.makeInnerSwapMessage({
          tokenDeltas: [
            ...Object.entries(tokenInDiff),
            [
              input.parsed.tokenOut.defuseAssetId,
              input.parsed.amountOut.amount,
            ],
          ],
          signerId: formatUserIdentity(input.signerCredentials),
          referral: input.referral,
          memo: "OTC_CREATE",
          deadlineTimestamp: Date.parse(deadline),
          appFee: primaryAppFee,
          appFeeRecipient,
        })

        if (transferIntents.length > 0) {
          innerMessage.intents ??= []
          innerMessage.intents.push(...transferIntents)
        }

        const walletMessage = messageFactory.makeSwapMessage({
          innerMessage,
          nonce: decodedNonce,
        })
        return { nonce: decodedNonce, walletMessage }
      }
    ),
  },
  actions: {
    logError: (_, event: { error: unknown }) => {
      logger.error(event.error)
    },
    complete: (
      { self },
      output:
        | SignIntentMachineOutput
        | { tag: "err"; value: FailedToPrepareMessageToSignError }
    ) => {
      self.send({ type: "COMPLETE", output })
    },
  },
}).createMachine({
  initial: "prepareData",

  context: ({ input }) => {
    return {
      ...input,
      nonce: null,
      walletMessage: null,
    }
  },

  output: ({ event }) => {
    return event.output as OTCMakerSignActorOutput
  },

  states: {
    prepareData: {
      invoke: {
        src: "prepareSignData",
        input: ({ context }) => context,
        onDone: {
          target: "signing",
          actions: assign(({ event, context }) => {
            return {
              ...context,
              nonce: event.output.nonce,
              walletMessage: event.output.walletMessage,
            }
          }),
        },
        onError: {
          actions: [
            { type: "logError", params: ({ event }) => event },
            {
              type: "complete",
              params: {
                tag: "err",
                value: { reason: "ERR_PREPARING_SIGNING_DATA" },
              },
            },
          ],
        },
      },

      on: {
        COMPLETE: "completed",
      },
    },

    signing: {
      invoke: {
        id: "signRef",
        src: "signActor",

        input: ({ context }) => {
          assert(context.walletMessage !== null)

          return {
            signMessage: context.signMessage,
            signerCredentials: context.signerCredentials,
            walletMessage: context.walletMessage,
          }
        },

        onError: {
          actions: [
            { type: "logError", params: ({ event }) => event },
            {
              type: "complete",
              params: {
                tag: "err",
                value: { reason: "EXCEPTION", error: null },
              },
            },
          ],
        },

        onDone: {
          actions: [
            {
              type: "complete",
              params: ({ event }) => event.output,
            },
          ],
        },
      },

      on: {
        COMPLETE: "completed",
      },
    },

    completed: {
      type: "final",
      output: ({ context, event }): OTCMakerSignActorOutput => {
        assertEvent(event, "COMPLETE")

        if (event.output.tag === "err") {
          return event.output
        }

        assert(context.nonce !== null)
        const multiPayload = formatSignedIntent(
          event.output.value.signatureResult,
          context.signerCredentials
        )

        return {
          tag: "ok",
          value: {
            multiPayload,
            signatureResult: event.output.value.signatureResult,
            signerCredentials: context.signerCredentials,
            usedNonceBase64: base64.encode(context.nonce),
          },
        }
      },
    },
  },
})
